import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Sparkles, FileText, ExternalLink, X } from 'lucide-react';

export interface AvisoSistemaModalAviso {
    id: string;
    titulo: string;
    mensagem: string;
    modelo: 'risco' | 'novidade';
    bloqueante: boolean;
    url_anexo?: string | null;
    /** null/undefined = "todo o staff". Mostrado no modal pra quem recebe avisos de
     * departamento e de "todo o staff" na mesma fila entender de onde cada um veio. */
    departamento_alvo?: string | null;
}

interface AvisoSistemaModalProps {
    aviso: AvisoSistemaModalAviso;
    posicao: number;
    total: number;
    onVisualizado: () => void;
    onConfirmar: () => Promise<void> | void;
    onFechar?: () => void;
}

const MODELOS: Record<AvisoSistemaModalAviso['modelo'], { label: string; color: string; Icon: typeof AlertTriangle }> = {
    risco: { label: 'Risco', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', Icon: AlertTriangle },
    novidade: { label: 'Novidade', color: 'bg-primary/10 text-primary dark:bg-primary/20', Icon: Sparkles },
};

const EXTENSOES_IMAGEM = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

const extensaoAnexo = (url: string): string => {
    const semQuery = url.split('?')[0].split('#')[0];
    const match = semQuery.match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toLowerCase() : '';
};

const AvisoSistemaModal = ({ aviso, posicao, total, onVisualizado, onConfirmar, onFechar }: AvisoSistemaModalProps) => {
    const [lido, setLido] = useState(false);
    const [aceite, setAceite] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [anexoAberto, setAnexoAberto] = useState(false);
    const jaMarcouVisto = useRef(false);
    const corpoRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    const modeloCfg = MODELOS[aviso.modelo];
    const extensao = aviso.url_anexo ? extensaoAnexo(aviso.url_anexo) : '';
    const anexoEhPdf = extensao === 'pdf';
    const anexoEhImagem = EXTENSOES_IMAGEM.includes(extensao);
    const anexoEmbutivel = anexoEhPdf || anexoEhImagem;
    const anexoPendente = !!aviso.url_anexo && !anexoEmbutivel && !anexoAberto;

    useEffect(() => {
        if (!jaMarcouVisto.current) {
            jaMarcouVisto.current = true;
            onVisualizado();
        }
    }, [onVisualizado]);

    useEffect(() => {
        dialogRef.current?.focus();
    }, []);

    // Quando o conteúdo já cabe no corpo do modal sem precisar de scroll (aviso curto,
    // sem anexo), o evento onScroll abaixo nunca dispara — "role até o fim" ficaria
    // permanentemente desabilitado e, sendo bloqueante, ninguém do staff conseguiria
    // fechar o modal. ResizeObserver reavalia sempre que o corpo muda de tamanho
    // (inclui imagem/PDF embutido que só define a altura final depois de carregar).
    useEffect(() => {
        const el = corpoRef.current;
        if (!el) return;
        const verificarSeJaCabe = () => {
            if (el.scrollHeight - el.clientHeight < 24) setLido(true);
        };
        verificarSeJaCabe();
        const observer = new ResizeObserver(verificarSeJaCabe);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const aoRolar = () => {
        const el = corpoRef.current;
        if (!el || lido) return;
        const chegouAoFim = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        if (chegouAoFim) setLido(true);
    };

    const confirmar = async () => {
        setEnviando(true);
        try {
            await onConfirmar();
        } finally {
            setEnviando(false);
        }
    };

    const aoTeclar = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            if (!onFechar) e.preventDefault();
            return;
        }
        if (e.key !== 'Tab') return;
        const focaveis = dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focaveis || focaveis.length === 0) return;
        const primeiro = focaveis[0];
        const ultimo = focaveis[focaveis.length - 1];
        if (e.shiftKey && document.activeElement === primeiro) {
            e.preventDefault();
            ultimo.focus();
        } else if (!e.shiftKey && document.activeElement === ultimo) {
            e.preventDefault();
            primeiro.focus();
        }
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4 animate-fade-in"
            onClick={onFechar}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="aviso-sistema-modal-titulo"
                tabIndex={-1}
                onKeyDown={aoTeclar}
                className="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 pt-6 pb-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            {total > 1 && (
                                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                                    Aviso {posicao} de {total}
                                </p>
                            )}
                            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${modeloCfg.color}`}>
                                <modeloCfg.Icon size={13} />
                                {modeloCfg.label}
                            </span>
                            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mt-1.5">
                                Para: {aviso.departamento_alvo || 'Todo o staff'}
                            </p>
                        </div>
                        {onFechar && (
                            <button onClick={onFechar} className="p-2 -mr-2 -mt-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0">
                                <X size={20} />
                            </button>
                        )}
                    </div>
                    <h2 id="aviso-sistema-modal-titulo" className="text-xl font-bold text-gray-900 dark:text-white mt-3">{aviso.titulo}</h2>
                </div>

                <div
                    ref={corpoRef}
                    onScroll={aoRolar}
                    tabIndex={0}
                    aria-label="Conteúdo do aviso — role até o fim, incluindo o anexo quando houver, para habilitar a confirmação"
                    className="px-4 pb-4 overflow-y-auto flex-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
                >
                    {aviso.mensagem}

                    {aviso.url_anexo && anexoEmbutivel && (
                        <div className="mt-4">
                            {anexoEhPdf ? (
                                <iframe
                                    src={`${aviso.url_anexo}#view=FitH&toolbar=0`}
                                    title="Documento anexo"
                                    className="w-full h-[420px] sm:h-[560px] rounded-xl border border-gray-200 dark:border-gray-700"
                                />
                            ) : (
                                <img
                                    src={aviso.url_anexo}
                                    alt="Documento anexo"
                                    className="w-full rounded-xl border border-gray-200 dark:border-gray-700 object-contain"
                                />
                            )}
                            <a
                                href={aviso.url_anexo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80"
                            >
                                Abrir em nova aba
                                <ExternalLink size={12} />
                            </a>
                        </div>
                    )}

                    {aviso.url_anexo && !anexoEmbutivel && (
                        <a
                            href={aviso.url_anexo}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setAnexoAberto(true)}
                            className={`mt-4 flex items-center gap-2 text-sm font-semibold ${anexoAberto ? 'text-gray-400' : 'text-primary hover:text-primary/80'}`}
                        >
                            <FileText size={16} />
                            {anexoAberto ? 'Documento anexo aberto' : 'Abrir documento anexo (obrigatório antes de continuar)'}
                            <ExternalLink size={13} />
                        </a>
                    )}
                </div>

                <div className="px-4 py-5 border-t border-gray-100 dark:border-gray-700 space-y-4">
                    <label className={`flex items-start gap-3 text-sm ${lido && !anexoPendente ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}`}>
                        <input
                            type="checkbox"
                            checked={aceite}
                            disabled={!lido || anexoPendente}
                            onChange={(e) => setAceite(e.target.checked)}
                            className="mt-0.5 w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary/30 disabled:opacity-50"
                        />
                        <span>
                            Li e concordo com o comunicado acima.
                            {!lido && <span className="block text-xs mt-0.5">Role até o fim do texto para habilitar.</span>}
                            {lido && anexoPendente && <span className="block text-xs mt-0.5">Abra o documento anexo acima para habilitar.</span>}
                        </span>
                    </label>
                    <span aria-live="polite" className="sr-only">
                        {lido && !anexoPendente && 'Aviso e anexo vistos até o fim. A caixa de confirmação está liberada.'}
                    </span>

                    <button
                        onClick={confirmar}
                        disabled={!lido || anexoPendente || !aceite || enviando}
                        className="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-full text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed transition-all"
                    >
                        {enviando ? 'Confirmando...' : 'Li e aceito'}
                    </button>

                    {onFechar && (
                        <button
                            onClick={onFechar}
                            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            Ver depois
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AvisoSistemaModal;
